import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Eye, Package, CheckCircle, Clock, Truck, RotateCcw, XCircle, Download } from 'lucide-react';
import MainService from '../../services/MainService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { flattenFiltersForApi } from '../../utils/listFilters';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;

const formatDate = (d) => {
  if (d == null || d === '') return '-';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
  }
  return '-';
};

const buildColumns = (ui) => [
  { field: 'voucherno', title: ui('columns.voucherNo'), widthClass: 'w-[10%]', hideable: true },
  { field: 'customername', title: ui('columns.customer'), widthClass: 'w-[12%]', hideable: true },
  { field: 'customermobile', title: ui('columns.mobile'), widthClass: 'w-[10%]', hideable: true },
  { field: 'clientname', title: ui('columns.client'), widthClass: 'w-[12%]', hideable: true },
  { field: 'status', title: ui('common.status'), widthClass: 'w-[10%]', hideable: true },
  { field: 'emirate', title: ui('columns.emirate'), widthClass: 'w-[10%]', hideable: true },
  { field: 'area', title: ui('columns.area'), widthClass: 'w-[10%]', hideable: true },
  { field: 'deliverydate', title: ui('columns.deliveryDate'), widthClass: 'w-[10%]', hideable: true },
  { field: 'actions', title: ui('columns.action'), widthClass: 'w-[8%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const defaultVisibility = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const getCellValue = (row, field) => {
  switch (field) {
    case 'voucherno':
      return row.VoucherNo || '-';
    case 'customername':
      return row.CustomerName || '-';
    case 'customermobile':
      return row.CustomerMobile || '-';
    case 'clientname':
      return row.ClientName || '-';
    case 'status':
      return row.Status || '-';
    case 'emirate':
      return row.Emirate || '-';
    case 'area':
      return row.Area || '-';
    case 'deliverydate':
      return formatDate(row.DeliveryDate);
    default:
      return row[field];
  }
};

const TodaysOrders = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const navigate = useNavigate();
  const ORDER_COLUMNS = useMemo(() => buildColumns(ui), [ui]);

  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [emirateWise, setEmirateWise] = useState([]);
  const [areaWise, setAreaWise] = useState([]);
  const [statusWise, setStatusWise] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [listSuccess, setListSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() => defaultVisibility(buildColumns(getAppUi('en'))));
  const [groupField, setGroupField] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  useEffect(() => {
    setColumnVisibility((prev) => {
      const next = defaultVisibility(ORDER_COLUMNS);
      ORDER_COLUMNS.forEach((col) => {
        if (prev[col.field] !== undefined) next[col.field] = prev[col.field];
      });
      return next;
    });
  }, [ORDER_COLUMNS]);

  useEffect(() => {
    if (listError) {
      const timer = setTimeout(() => setListError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [listError]);

  useEffect(() => {
    if (listSuccess) {
      const timer = setTimeout(() => setListSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [listSuccess]);

  const fetchList = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
  } = {}) => {
    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const seq = ++loadSeqRef.current;
    const isAll = size === 'all';

    try {
      setListLoading(true);
      setListError('');
      const body = {
        Skip: isAll ? 0 : (pageNum - 1) * Number(size),
        Take: isAll ? 0 : Number(size),
        Keyword: null,
        SortField: sField || null,
        SortDir: sDir || 'asc',
        Filters: flattenFiltersForApi(filters),
      };

      const response = await MainService.TodaysDeliveryListList(body);
      if (seq !== loadSeqRef.current) return;
      const rows = parseList(response);
      setList(rows);
      setTotalCount(parseTotal(response, rows.length));
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error("Error loading today's orders:", err);
      setList([]);
      setTotalCount(0);
      setListError(ui('orders.todaysOrders.noOrdersToday'));
    } finally {
      if (seq === loadSeqRef.current) setListLoading(false);
    }
  }, [page, pageSize, sortField, sortDirection, filtersByField, ui]);

  const loadStatsAndCharts = useCallback(async () => {
    try {
      const [statsRes, emirateRes, areaRes, statusRes] = await Promise.all([
        MainService.TodaysDeliveryStatistics(),
        MainService.EmirateWiseDelivery(),
        MainService.AreaWiseOrders(),
        MainService.StatusWiseOrders(),
      ]);
      setStats(statsRes);
      setEmirateWise(Array.isArray(emirateRes) ? emirateRes : []);
      setAreaWise(Array.isArray(areaRes) ? areaRes : []);
      setStatusWise(Array.isArray(statusRes) ? statusRes : []);
    } catch (err) {
      console.error("Error loading today's stats:", err);
      setStats(null);
      setEmirateWise([]);
      setAreaWise([]);
      setStatusWise([]);
    }
  }, []);

  useEffect(() => {
    loadStatsAndCharts().finally(() => setStatsLoading(false));
  }, [loadStatsAndCharts]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => ORDER_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [ORDER_COLUMNS, columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows: list }];
    const mapped = list.map((r) => ({ ...r, [groupField]: getCellValue(r, groupField) }));
    return groupRows(mapped, groupField);
  }, [list, groupField]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const statCards = useMemo(
    () => [
      { key: 'TotalOrders', labelPath: 'orders.todaysOrders.statTotalOrders', icon: Package, color: 'bg-blue-50 text-blue-700 border-blue-200' },
      { key: 'Delivered', labelPath: 'orders.todaysOrders.statDelivered', icon: CheckCircle, color: 'bg-green-50 text-green-700 border-green-200' },
      { key: 'Pending', labelPath: 'orders.todaysOrders.statPending', icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200' },
      { key: 'TRANSIT', labelPath: 'orders.todaysOrders.statInTransit', icon: Truck, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
      { key: 'RETURN', labelPath: 'orders.todaysOrders.statReturn', icon: RotateCcw, color: 'bg-orange-50 text-orange-700 border-orange-200' },
      { key: 'Cancelled', labelPath: 'orders.todaysOrders.statCancelled', icon: XCircle, color: 'bg-red-50 text-red-700 border-red-200' },
    ],
    []
  );

  const handleRefresh = () => {
    setStatsLoading(true);
    setPage(1);
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    Promise.all([
      fetchList({
        page: 1,
        sortField: '',
        sortDirection: 'asc',
        filters: {},
      }),
      loadStatsAndCharts(),
    ]).finally(() => setStatsLoading(false));
  };

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
    }
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
    }
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchList({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchList({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchList({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = ORDER_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    const nextFilters = { ...filtersByField, [field]: filter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchList({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchList({ page: 1, filters: nextFilters });
  };

  const clearSortAndFilters = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setFiltersByField({});
    setSortField('');
    setSortDirection('asc');
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    setPage(1);
    if (prev && prev.pageSize !== pageSize) {
      setPageSize(prev.pageSize);
    }
    fetchList({
      page: 1,
      pageSize: prev?.pageSize ?? pageSize,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const exportToExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: "Today's Orders",
        fileName: `todays_orders_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', ...ORDER_COLUMNS.filter((c) => c.field !== 'actions').map((c) => c.title)],
        groups: displayGroups,
        groupFieldTitle: ORDER_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (row, serial) => [
          serial,
          ...ORDER_COLUMNS.filter((c) => c.field !== 'actions').map((c) => getCellValue(row, c.field)),
        ],
      });
      setListSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      if (err?.message === 'No rows to export') {
        setListError(ui('orders.todaysOrders.noOrdersToday'));
      } else {
        setListError('Failed to export Excel file');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'todaysOrders')}</h1>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={statsLoading || listLoading}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-70"
        >
          <RefreshCw className={`w-4 h-4 ${statsLoading || listLoading ? 'animate-spin' : ''}`} />
          {ui('common.refresh')}
        </button>
      </div>

      {statsLoading && !stats ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : (
        <>
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {statCards.map(({ key, labelPath, icon: Icon, color }) => (
                <div key={key} className={`rounded-xl border p-4 ${color}`}>
                  <Icon className="w-5 h-5 mb-2 opacity-80" />
                  <p className="text-2xl font-bold">{stats[key] ?? 0}</p>
                  <p className="text-sm opacity-90">{ui(labelPath)}</p>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{ui('orders.todaysOrders.byEmirate')}</h2>
              {emirateWise.length === 0 ? (
                <p className="text-sm text-gray-500">{ui('orders.todaysOrders.noData')}</p>
              ) : (
                <ul className="space-y-2">
                  {emirateWise.map((row, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span className="text-gray-700">{row.Emirate || '-'}</span>
                      <span className="font-medium text-gray-900">{row.TotalOrders ?? 0}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{ui('orders.todaysOrders.byArea')}</h2>
              {areaWise.length === 0 ? (
                <p className="text-sm text-gray-500">{ui('orders.todaysOrders.noData')}</p>
              ) : (
                <ul className="space-y-2">
                  {areaWise.map((row, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span className="text-gray-700">{row.Area || '-'}</span>
                      <span className="font-medium text-gray-900">{row.TotalOrders ?? 0}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{ui('orders.todaysOrders.byStatus')}</h2>
              {statusWise.length === 0 ? (
                <p className="text-sm text-gray-500">{ui('orders.todaysOrders.noData')}</p>
              ) : (
                <ul className="space-y-2">
                  {statusWise.map((row, i) => (
                    <li key={i} className="flex justify-between items-center text-sm gap-2">
                      <span
                        className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: row.Color ? `${row.Color}20` : '#e5e7eb',
                          color: row.Color || '#374151',
                        }}
                      >
                        {row.Status || '-'}
                      </span>
                      <span className="font-medium text-gray-900">{row.TotalOrders ?? 0}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{ui('orders.todaysOrders.orderListHeading')}</h2>
              <button
                type="button"
                onClick={exportToExcel}
                disabled={list.length === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>

            {listSuccess && (
              <div className="mx-4 mt-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{listSuccess}</div>
            )}
            {listError && (
              <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{listError}</div>
            )}

            <div className="master-table-scroll relative overflow-x-auto" style={{ maxHeight: 560 }}>
              {listLoading && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-2">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                    <span className="text-sm text-gray-600">{ui('common.loading') || 'Loading...'}</span>
                  </div>
                </div>
              )}

              {(activeFilterCount > 0 || sortField) && (
                <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
                  <span>
                    {sortField
                      ? `Sorted by ${ORDER_COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                      : 'Filters active'}
                    {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
                  </span>
                  <button type="button" onClick={clearSortAndFilters} className="text-primary hover:underline">
                    Clear sort & filters
                  </button>
                </div>
              )}

              {groupField && (
                <GroupByBanner
                  groupTitle={ORDER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
                  groupKeys={groupKeys}
                  onUngroup={handleUngroup}
                  onCollapseAll={collapseAll}
                  onExpandAll={expandAll}
                />
              )}

              <table className="w-full min-w-[900px] table-fixed">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    <th className="w-[4%] px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                    {ORDER_COLUMNS.map((column) =>
                      columnVisibility[column.field] === false ? null : (
                        <DataColumnMenu
                          key={column.field}
                          column={column}
                          columns={ORDER_COLUMNS}
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleColumnSort}
                          columnVisibility={columnVisibility}
                          onToggleColumn={handleToggleColumn}
                          filtersByField={filtersByField}
                          onApplyFilter={handleApplyColumnFilter}
                          onClearFilter={handleClearColumnFilter}
                          groupField={groupField}
                          onGroupBy={handleGroupBy}
                          onUngroup={handleUngroup}
                          align={column.align}
                        />
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {list.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan} className="px-4 py-8 text-center text-gray-500">
                        {ui('orders.todaysOrders.noOrdersToday')}
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      let serial = 0;
                      return displayGroups.flatMap((group) => {
                        const block = [];
                        if (group.label != null) {
                          block.push(
                            <tr key={`group-${group.key}`} className="bg-gray-100">
                              <td colSpan={colSpan} className="px-4 py-2">
                                <GroupHeaderLabel
                                  title={ORDER_COLUMNS.find((c) => c.field === groupField)?.title}
                                  label={group.label}
                                  count={group.rows.length}
                                  collapsed={isCollapsed(group.key)}
                                  onToggle={() => toggleGroup(group.key)}
                                />
                              </td>
                            </tr>
                          );
                        }
                        if (group.label != null && isCollapsed(group.key)) {
                          return block;
                        }
                        group.rows.forEach((row, index) => {
                          serial += 1;
                          block.push(
                            <tr key={row.Id || `${group.key}-${index}`} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                                {groupField || pageSize === 'all' ? serial : (page - 1) * Number(pageSize) + serial}
                              </td>
                              {ORDER_COLUMNS.map((column) =>
                                columnVisibility[column.field] === false ? null : (
                                  <td
                                    key={column.field}
                                    className={`px-4 py-2 text-sm ${
                                      column.field === 'voucherno' ? 'font-medium text-gray-900 whitespace-nowrap' : 'text-gray-600'
                                    } ${column.field === 'actions' ? 'text-center whitespace-nowrap' : ''}`}
                                  >
                                    {column.field === 'actions' ? (
                                      <button
                                        type="button"
                                        onClick={() => navigate(`/home/orders/${row.Id}`)}
                                        className="inline-flex items-center gap-1 text-primary hover:text-green-700 text-sm font-medium"
                                      >
                                        <Eye className="w-4 h-4" />
                                        {ui('common.view')}
                                      </button>
                                    ) : (
                                      getCellValue(row, column.field)
                                    )}
                                  </td>
                                )
                              )}
                            </tr>
                          );
                        });
                        return block;
                      });
                    })()
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
                <div className="flex flex-wrap items-center gap-3">
                  <span>
                    {totalCount === 0
                      ? ui('orders.todaysOrders.noOrdersToday')
                      : ui('orders.todaysOrders.showingToOf', { from: rangeFrom, to: rangeTo, total: totalCount })}
                    {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                    {groupField
                      ? ` · grouped by ${ORDER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}`
                      : ''}
                  </span>
                  <label className="flex items-center gap-2">
                    <span className="text-gray-500">Rows</span>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      disabled={listLoading || !!groupField}
                      className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                    >
                      {PAGE_SIZE_OPTIONS.map((opt) => (
                        <option key={String(opt)} value={opt}>
                          {opt === 'all' ? 'All' : opt}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {!groupField && pageSize !== 'all' && (
                  <div className="flex flex-wrap items-center gap-1">
                    <button
                      type="button"
                      disabled={page <= 1 || listLoading}
                      onClick={() => goToPage(page - 1)}
                      className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {ui('common.previous')}
                    </button>
                    {pageItems.map((item, idx) =>
                      item === '…' ? (
                        <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          disabled={listLoading}
                          onClick={() => goToPage(item)}
                          className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                            item === page
                              ? 'bg-[#46955A] font-medium text-white'
                              : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                          }`}
                        >
                          {item}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      disabled={page >= totalPages || listLoading}
                      onClick={() => goToPage(page + 1)}
                      className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {ui('common.next')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TodaysOrders;
